import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, CheckCircle, Clock, FileText, Building, PenTool, MessageSquare } from 'lucide-react'

interface VerificationPromptProps {
  userStatus?: {
    isVerified: boolean
    verificationStep: 'company' | 'license' | 'signature' | 'template' | 'completed'
    completedSteps: string[]
  }
  className?: string
}

const verificationSteps = [
  {
    id: 'company',
    title: '企业信息',
    description: '填写企业基本信息',
    icon: Building,
  },
  {
    id: 'license',
    title: '营业执照',
    description: '上传营业执照进行认证',
    icon: FileText,
  },
  {
    id: 'signature',
    title: '签名报备',
    description: '创建并提交短信签名',
    icon: PenTool,
  },
  {
    id: 'template',
    title: '模板申请',
    description: '创建并提交短信模板',
    icon: MessageSquare,
  },
]

export default function VerificationPrompt({ userStatus, className = '' }: VerificationPromptProps) {
  if (userStatus?.isVerified) {
    return null
  }

  const currentStep = userStatus?.verificationStep || 'company'
  const completedSteps = userStatus?.completedSteps || []

  return (
    <Card className={`border-amber-200 bg-amber-50 ${className}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <CardTitle className="text-amber-800">完成企业认证以使用完整功能</CardTitle>
        </div>
        <CardDescription className="text-amber-700">
          认证完成后，您将可以发送短信、管理模板和签名等功能
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* 认证步骤进度 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {verificationSteps.map((step) => {
            const isCompleted = completedSteps.includes(step.id)
            const isCurrent = step.id === currentStep
            const Icon = step.icon
            
            return (
              <div
                key={step.id}
                className={`p-3 rounded-lg border-2 transition-all ${
                  isCompleted
                    ? 'border-green-200 bg-green-50'
                    : isCurrent
                    ? 'border-amber-300 bg-amber-100'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <Icon className={`h-4 w-4 ${
                    isCompleted ? 'text-green-600' : isCurrent ? 'text-amber-600' : 'text-gray-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    isCompleted ? 'text-green-800' : isCurrent ? 'text-amber-800' : 'text-gray-600'
                  }`}>
                    {step.title}
                  </span>
                  {isCompleted && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                  {isCurrent && (
                    <Clock className="h-4 w-4 text-amber-600" />
                  )}
                </div>
                <p className={`text-xs ${
                  isCompleted ? 'text-green-700' : isCurrent ? 'text-amber-700' : 'text-gray-500'
                }`}>
                  {step.description}
                </p>
              </div>
            )
          })}
        </div>

        {/* 操作按钮 */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Link to="/onboarding" className="flex-1">
            <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white">
              {completedSteps.length === 0 ? '开始认证' : '继续认证'}
            </Button>
          </Link>
          <Link to="/help/verification" className="flex-1">
            <Button variant="outline" className="w-full border-amber-300 text-amber-700 hover:bg-amber-100">
              认证帮助
            </Button>
          </Link>
        </div>

        {/* 认证进度 */}
        <div className="pt-2">
          <div className="flex items-center justify-between text-sm text-amber-700 mb-1">
            <span>认证进度</span>
            <span>{completedSteps.length}/4 步骤</span>
          </div>
          <div className="w-full bg-amber-200 rounded-full h-2">
            <div
              className="bg-amber-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedSteps.length / 4) * 100}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}